import { FlexBox, IconButton, InputText } from '@dixa/lib-shared-habla';
import {
  ChangeEvent,
  forwardRef,
  ForwardRefRenderFunction,
  memo,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import { useUpdateEffect } from 'react-use';

type AnalyticsFilterSearchProps = {
  value: string;
  isOpen: boolean;
  isDisabled: boolean;
  onChange: (value: string) => void;
};

const AnalyticsFilterSearch: ForwardRefRenderFunction<
  HTMLInputElement,
  AnalyticsFilterSearchProps
> = ({ value, isOpen, isDisabled, onChange }, forwardedRef) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
    forwardedRef,
    () => inputRef.current,
  );

  useLayoutEffect(() => {
    if (isOpen) {
      // This has to be done in the next event loop frame through a timeout as
      // the DOM elements would otherwise not be ready.
      setTimeout(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen, onChange]);

  useUpdateEffect(() => {
    if (!isOpen) {
      onChange('');
    }
  }, [isOpen]);

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const handleClearSearch = () => {
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <InputText
      ref={inputRef}
      value={value}
      onChange={handleSearch}
      placeholder="Search..."
      disabled={isDisabled}
      suffix={{
        type: 'custom',
        value: (
          <FlexBox alignItems="center" padding="0 100">
            <IconButton
              variant="tertiary"
              icon={value ? 'close' : 'search'}
              onClick={value ? handleClearSearch : undefined}
            />
          </FlexBox>
        ),
        noWrap: true,
      }}
    />
  );
};

export const AnalyticsFilterSearchMemo = memo(
  forwardRef(AnalyticsFilterSearch),
);

export { AnalyticsFilterSearchMemo as AnalyticsFilterSearch };
